import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { quizService } from '../../services/quizService';
import { useAuth } from '../Auth/AuthProvider';
import QuizQuestion from '../QuizQuestion';
import QuestionResult from '../QuestionResult';
import QuizResults from '../QuizResults';
import QuizSettings from '../QuizSettings';
import HamburgerMenu from '../Common/HamburgerMenu';
import logger from '../../utils/logger';
import './SharedQuiz.css';

const SharedQuiz = ({ userId, quizId }) => {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [shuffledChoices, setShuffledChoices] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [showingResult, setShowingResult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime] = useState(new Date());
  const [showSettings, setShowSettings] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const questionStartRef = useRef(null);

  useEffect(() => {
    logger.componentMount('SharedQuiz');
    logger.userAction('start_shared_quiz', { quizId, userId });
    
    // Check for skip-settings query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const shouldSkipSettings = urlParams.get('skip-settings') === 'true';
    
    if (shouldSkipSettings) {
      setShowSettings(false);
      logger.info('Settings will be skipped based on query parameter', { quizId, userId }, 'COMPONENT');
    }
    
    // Define handleStartQuizWithDefaultSettings inside useEffect to avoid dependency issues
    const handleStartQuizWithDefaultSettings = (questionsData, settings) => {
      logger.userAction('start_shared_quiz_with_default_settings', { 
        quizId, 
        settings 
      });

      // Apply quiz settings to filter and prepare questions
      let filteredQuestions = [...questionsData];
      
      // Apply question count limit
      if (settings.questionCount < filteredQuestions.length) {
        if (settings.orderMode === 'random') {
          filteredQuestions = quizService.shuffleArray(filteredQuestions).slice(0, settings.questionCount);
        } else {
          filteredQuestions = filteredQuestions.slice(0, settings.questionCount);
        }
      }

      // Apply order mode
      if (settings.orderMode === 'random') {
        filteredQuestions = quizService.shuffleArray(filteredQuestions);
      }

      // Update questions state with filtered questions
      setQuestions(filteredQuestions);
      
      // Shuffle choices for each question
      const shuffledChoicesMap = {};
      filteredQuestions.forEach(question => {
        if (question?.id && question?.choices) {
          shuffledChoicesMap[question.id] = quizService.shuffleArray(question.choices);
        }
      });
      setShuffledChoices(shuffledChoicesMap);
      
      // Initialize user answers
      setUserAnswers(new Array(filteredQuestions.length).fill(null));
      
      // Start the quiz
      setShowSettings(false);
      setQuizStarted(true);
    };
    
    const loadSharedQuiz = async () => {
      try {
        setLoading(true);
        logger.startTimer('shared_quiz_load');

        // Load quiz data
        const quizRef = doc(db, 'users', userId, 'quizzes', quizId);
        const quizSnap = await getDoc(quizRef);

        if (!quizSnap.exists()) {
          throw new Error('クイズが見つかりません');
        }

        const quizData = { ...quizSnap.data(), id: quizSnap.id };
        
        // Check if quiz is public or shared
        if (quizData.visibility === 'private') {
          throw new Error('このクイズは非公開です');
        }

        setQuiz(quizData);

        // Load questions
        const questionsRef = collection(db, 'users', userId, 'quizzes', quizId, 'questions');
        const q = query(questionsRef, orderBy('order', 'asc'));
        const questionsSnap = await getDocs(q);

        const questionsData = [];
        questionsSnap.forEach((doc) => {
          questionsData.push({ ...doc.data(), id: doc.id });
        });

        if (questionsData.length === 0) {
          throw new Error('このクイズには質問がありません');
        }

        setQuestions(questionsData);
        
        // If settings should be skipped, auto-start quiz with default settings
        if (shouldSkipSettings) {
          const defaultSettings = {
            questionCount: Math.min(10, questionsData.length),
            orderMode: 'random',
            continueMode: true,
            startFromQuestion: null,
            answeredQuestions: [],
            incorrectQuestions: []
          };
          
          logger.info('Auto-starting quiz with default settings', { 
            quizId, 
            defaultSettings 
          }, 'COMPONENT');
          
          // Apply default settings to start quiz immediately
          setTimeout(() => {
            handleStartQuizWithDefaultSettings(questionsData, defaultSettings);
          }, 0);
        }
        
        // We'll initialize shuffled choices and user answers when quiz starts
        // to allow for different question counts and ordering based on settings

        const loadDuration = logger.endTimer('shared_quiz_load');
        logger.info(`Shared quiz loaded successfully`, { 
          quizId,
          quizTitle: quizData.title,
          totalQuestions: questionsData.length,
          loadDuration,
          skipSettings: shouldSkipSettings
        }, 'COMPONENT');
        
      } catch (error) {
        logger.error('Failed to load shared quiz', { error: error.message, userId, quizId });
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId && quizId) {
      loadSharedQuiz();
    }
    
    return () => {
      logger.componentUnmount('SharedQuiz');
    };
  }, [userId, quizId]);

  const handleStartQuiz = (settings) => {
    logger.userAction('start_shared_quiz_with_settings', { 
      quizId, 
      settings 
    });

    // Apply quiz settings to filter and prepare questions
    let filteredQuestions = [...questions];
    
    // Apply question count limit
    if (settings.questionCount < filteredQuestions.length) {
      if (settings.orderMode === 'random') {
        filteredQuestions = quizService.shuffleArray(filteredQuestions).slice(0, settings.questionCount);
      } else {
        filteredQuestions = filteredQuestions.slice(0, settings.questionCount);
      }
    }

    // Apply order mode
    if (settings.orderMode === 'random') {
      filteredQuestions = quizService.shuffleArray(filteredQuestions);
    }

    // Update questions state with filtered questions
    setQuestions(filteredQuestions);
    
    // Shuffle choices for each question
    const shuffledChoicesMap = {};
    filteredQuestions.forEach(question => {
      if (question?.id && question?.choices) {
        shuffledChoicesMap[question.id] = quizService.shuffleArray(question.choices);
      }
    });
    setShuffledChoices(shuffledChoicesMap);
    
    // Initialize user answers
    setUserAnswers(new Array(filteredQuestions.length).fill(null));
    
    // Start the quiz
    setShowSettings(false);
    setQuizStarted(true);
  };

  const handleAnswer = (selectedChoiceIds) => {
    const newAnswers = [...userAnswers];
    const currentQuestion = questions[currentQuestionIndex];
    
    // Check if answer is correct
    const correctChoiceIds = currentQuestion.choices
      .filter(choice => choice.isCorrect)
      .map(choice => choice.id);
    
    const isCorrect = 
      selectedChoiceIds.length === correctChoiceIds.length &&
      selectedChoiceIds.every(id => correctChoiceIds.includes(id));
    
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestion.id,
      shuffledChoiceOrder: shuffledChoices[currentQuestion.id]?.map(choice => choice.id) || [],
      selectedChoiceIds,
      isCorrect
    };
    
    setUserAnswers(newAnswers);
    
    logger.userAction('answer_shared_quiz_question', {
      quizId,
      questionId: currentQuestion.id,
      questionIndex: currentQuestionIndex + 1,
      selectedChoices: selectedChoiceIds,
      isCorrect,
      questionType: currentQuestion.questionType
    });

    // Show result immediately after answer
    setShowingResult(true);
  };

  const handleNextFromResult = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowingResult(false);
      logger.userAction('navigate_next_question_shared', {
        quizId,
        fromQuestion: currentQuestionIndex + 1,
        toQuestion: currentQuestionIndex + 2
      });
      // Scroll to question start when moving to next question
      setTimeout(() => {
        questionStartRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      logger.userAction('complete_shared_quiz_attempt', { quizId });
      completeQuiz();
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      logger.userAction('navigate_next_question_shared', {
        quizId,
        fromQuestion: currentQuestionIndex + 1,
        toQuestion: currentQuestionIndex + 2
      });
      // Scroll to question start when moving to next question
      setTimeout(() => {
        questionStartRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      logger.userAction('complete_shared_quiz_attempt', { quizId });
      completeQuiz();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      logger.userAction('navigate_previous_question_shared', {
        quizId,
        fromQuestion: currentQuestionIndex + 1,
        toQuestion: currentQuestionIndex
      });
      // Scroll to question start when moving to previous question
      setTimeout(() => {
        questionStartRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const completeQuiz = async () => {
    try {
      const endTime = new Date();
      const duration = endTime - startTime;
      const score = quizService.calculateScore(userAnswers);
      
      logger.info(`Shared quiz completed`, {
        quizId,
        userId,
        score,
        totalQuestions: userAnswers.length,
        duration: duration,
        percentage: Math.round((score / userAnswers.length) * 100),
        isLoggedIn: !!user
      }, 'COMPONENT');
      
      // Save attempt to database if user is logged in
      if (user) {
        const attemptData = {
          userId: user.uid,
          startedAt: startTime.toISOString(),
          finishedAt: endTime.toISOString(),
          answers: userAnswers,
          score
        };
        
        try {
          await quizService.saveSharedQuizAttempt(user.uid, userId, quizId, attemptData);
          logger.info('Saved shared quiz attempt', { 
            loggedInUserId: user.uid, 
            originalUserId: userId, 
            quizId,
            score 
          }, 'COMPONENT');
        } catch (saveError) {
          logger.error('Failed to save shared quiz attempt', {
            loggedInUserId: user.uid,
            originalUserId: userId,
            quizId,
            saveError: saveError.message
          }, 'COMPONENT');
          // Continue even if save fails
        }
      } else {
        logger.info('Shared quiz completed without saving (user not logged in)', {
          quizId,
          userId,
          score
        }, 'COMPONENT');
      }
      
      setIsCompleted(true);
      
    } catch (err) {
      logger.error('Failed to complete shared quiz', {
        quizId,
        userId,
        error: err.message,
        stack: err.stack
      }, 'COMPONENT');
      setIsCompleted(true); // Still show results even if there are errors
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setIsCompleted(false);
    setShowingResult(false);
    setShowSettings(true);
    setQuizStarted(false);
  };

  if (loading) {
    return <div className="quiz-loading">クイズを読み込み中...</div>;
  }

  if (error) {
    return (
      <div className="quiz-error">
        <p>{error}</p>
        <button onClick={() => {
          if (user) {
            // ログイン済みの場合は通常のクイズ一覧へ
            window.location.href = '/';
          } else {
            // ログインしていない場合は公開クイズ一覧へ
            window.location.href = '/public';
          }
        }} className="back-btn">
          {user ? 'クイズ一覧に戻る' : '公開クイズ一覧に戻る'}
        </button>
      </div>
    );
  }

  // Show settings before starting the quiz
  if (showSettings && !quizStarted) {
    const settingsContent = (
      <QuizSettings
        quiz={quiz}
        totalQuestions={questions.length}
        onStartQuiz={handleStartQuiz}
        onCancel={() => {
          if (user) {
            // ログイン済みの場合は通常のクイズ一覧へ
            window.location.href = '/';
          } else {
            // ログインしていない場合は公開クイズ一覧へ
            window.location.href = '/public';
          }
        }}
        isPublicAccess={!user}
        isSharedQuiz={true}
        originalUserId={userId}
      />
    );

    // 公開クイズの設定画面には常にヘッダー/フッターを付与（未ログイン/ログイン問わず）
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-left">
            <HamburgerMenu />
          </div>
          <div className="header-title">
            <img src="/apple-touch-icon.png" alt="Quizenie Icon" className="app-icon" />
            <h1>Quizenie</h1>
          </div>
          <p>公開クイズ設定</p>
        </header>
        <main className="App-main">
          {settingsContent}
        </main>
        <footer className="App-footer">
          <p>&copy; 2025 Quizenie. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-left">
            <HamburgerMenu />
          </div>
          <div className="header-title">
            <img src="/apple-touch-icon.png" alt="Quizenie Icon" className="app-icon" />
            <h1>Quizenie</h1>
          </div>
          <p>公開クイズ</p>
        </header>
        <main className="App-main">
          <QuizResults 
            quiz={quiz}
            questions={questions}
            userAnswers={userAnswers}
            onRestart={() => {
              logger.userAction('restart_shared_quiz', { quizId });
              resetQuiz();
            }}
            onRestartWithSettings={() => {
              logger.userAction('restart_shared_quiz_with_settings', { quizId });
              resetQuiz();
            }}
            onBack={() => {
              if (user) {
                // ログイン済みの場合は通常のクイズ一覧へ
                window.location.href = '/';
              } else {
                // ログインしていない場合は公開クイズ一覧へ
                window.location.href = '/public';
              }
            }}
          />
        </main>
        <footer className="App-footer">
          <p>&copy; 2025 Quizenie. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = userAnswers[currentQuestionIndex];

  // Show result immediately after answering
  if (showingResult && currentAnswer && currentQuestion) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-left">
            <HamburgerMenu />
          </div>
          <div className="header-title">
            <img src="/apple-touch-icon.png" alt="Quizenie Icon" className="app-icon" />
            <h1>Quizenie</h1>
          </div>
          <p>公開クイズ</p>
        </header>
        <main className="App-main">
          <div ref={questionStartRef}>
            <QuestionResult
              question={currentQuestion}
              userAnswer={currentAnswer}
              shuffledChoices={shuffledChoices[currentQuestion.id] || []}
              onNext={handleNextFromResult}
              isLastQuestion={currentQuestionIndex === questions.length - 1}
              questionNumber={currentQuestionIndex + 1}
              totalQuestions={questions.length}
              onBack={() => {
                if (user) {
                  // ログイン済みの場合は通常のクイズ一覧へ
                  window.location.href = '/';
                } else {
                  // ログインしていない場合は公開クイズ一覧へ
                  window.location.href = '/public';
                }
              }}
            />
          </div>
        </main>
        <footer className="App-footer">
          <p>&copy; 2025 Quizenie. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <HamburgerMenu />
        </div>
        <div className="header-title">
          <img src="/apple-touch-icon.png" alt="Quizenie Icon" className="app-icon" />
          <h1>Quizenie</h1>
        </div>
        <p>公開クイズ</p>
      </header>
      <main className="App-main">
        <div className="quiz-container">
          <div className="quiz-header">
            <h1>{quiz.title}</h1>
            <button 
              onClick={() => {
                if (user) {
                  // ログイン済みの場合は通常のクイズ一覧へ
                  window.location.href = '/';
                } else {
                  // ログインしていない場合は公開クイズ一覧へ
                  window.location.href = '/public';
                }
              }}
              className="back-to-list-btn"
              title={user ? 'クイズ一覧に戻る' : '公開クイズ一覧に戻る'}
            >
              {user ? 'クイズ一覧に戻る' : '公開クイズ一覧に戻る'}
            </button>
            <div className="quiz-progress">
              <span>問題 {currentQuestionIndex + 1} / {questions.length}</span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div ref={questionStartRef}>
            <QuizQuestion
              question={currentQuestion}
              choices={shuffledChoices[currentQuestion?.id] || []}
              selectedChoices={currentAnswer ? currentAnswer.selectedChoiceIds : []}
              onSubmit={handleAnswer}
            />
          </div>

          <div className="quiz-navigation">
            <button 
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="nav-btn prev-btn"
            >
              前の問題
            </button>
            
            <button 
              onClick={handleNext}
              disabled={!currentAnswer}
              className="nav-btn next-btn"
            >
              {currentQuestionIndex === questions.length - 1 ? '結果を見る' : '次の問題'}
            </button>
          </div>
        </div>
      </main>
      <footer className="App-footer">
        <p>&copy; 2025 Quizenie. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default SharedQuiz;
